import { Conference } from "@easycv/common/types/conference";
import { User } from "@easycv/common/types/user";
import { Backend } from "@easycv/common/types/util";
import Awesomplete from "awesomplete";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat.js";
import _ from "lodash";
import {
  getNewQuestionKey,
  getQuestionCaption
} from "../../customSections/career.js";
import { escapeHtml, getProp } from "../../lib/frontendUtilities.js";
import { anyPathOverlap, anyPathWithin } from "../../lib/hierarchyUtilities.js";
import { for2, getKey } from "../../lib/utilities.js";
import {
  isAnyConferenceOpen,
  mostRecentOpenConference
} from "../conference_helper.js";
import { _defaultManageOptions, CCManageOptions } from "./types/manage";

dayjs.extend(customParseFormat); // enables dayjs(date, format, strict).isValid();

const loadingComponentHTML =
  `<div id="loadingSpinner" style="display: flex; align-items: center; justify-content:center; height: 100%;">` +
  `<div style="display: flex; width: min-content; flex-direction: column; padding: 1rem;">` +
  `<div style="border: 0.5rem solid #055da0; border-top: 0.5rem solid #eee; border-radius: 100%; width: 5rem; height: 5rem; animation: spin 1.5s linear infinite;">` +
  `</div>` +
  `</div>` +
  `</div>`;
export class CCManage {
  public users: { [key: string]: User };
  public saveConference: (arg1: Conference, arg2?: any) => any;
  public updateConfDate: (arg1: Conference) => any;
  public conference: Conference;
  public m_mentees: string[];
  public confMentorBox: Awesomplete;
  public options: CCManageOptions;
  public menteeDivisions: { [key: string]: string };

  private callOrderOnceUsersDefined: (() => void)[];

  private static __in_memory_user_lists: Record<string, Record<string, User>> =
    {};
  constructor(
    conference: Conference,
    saveConference: (arg1: Conference, arg2?: any) => any,
    updateConfDate: (arg1: Conference) => any,
    options?: CCManageOptions
  ) {
    $("#confSettings").empty();
    $("#confSettings").append(loadingComponentHTML);
    this.saveConference = saveConference;
    this.updateConfDate = updateConfDate;
    this.conference = conference;
    if (options == undefined) options = _defaultManageOptions;
    this.options = options;
    this.menteeDivisions = {};
    Object.keys(_defaultManageOptions).forEach((key) => {
      if (typeof _defaultManageOptions[key] === "object") {
        if (this.options[key] == undefined) {
          this.options[key] = _defaultManageOptions[key];
        } else {
          Object.keys(_defaultManageOptions[key]).forEach((subKey) => {
            this.options[key][subKey] ??= _defaultManageOptions[key][subKey];
          });
        }
      } else {
        this.options[key] ??= _defaultManageOptions[key];
      }
    });
    this.callOrderOnceUsersDefined = [];
    this.pullUserList();
  }

  private invalidateCachedTimestamp() {
    localStorage.removeItem(`${this.conference.id}_load_timestamp`);
    delete CCManage.__in_memory_user_lists[this.conference.id];
  }
  private setCachedTimestamp() {
    localStorage.setItem(
      `${this.conference.id}_load_timestamp`,
      String(Date.now())
    );
    CCManage.__in_memory_user_lists[this.conference.id] = {
      ...this.users
    };
  }
  /**
   * @returns true if valid cache, false otherwise
   */
  private checkCachedTimestamp() {
    if (CCManage.__in_memory_user_lists?.[this.conference.id] !== undefined) {
      const ccItem = localStorage.getItem(
        `${this.conference.id}_load_timestamp`
      );
      let timestamp = new Date(parseInt(ccItem));
      if (timestamp instanceof Date) {
        const manageItem = localStorage.getItem("manage_load_timestamp");
        let manageTimestamp = new Date(parseInt(manageItem));
        if (manageTimestamp instanceof Date) {
          if (timestamp > manageTimestamp) {
            return true;
          }
        }
      }
    }
    this.invalidateCachedTimestamp();
    return false;
  }
  public pullUserList() {
    void (async () => {
      // Check for cached
      if (this.checkCachedTimestamp()) {
        // copy the in memory list.
        // dont want a reference to keep things simple
        this.users = {
          ...CCManage.__in_memory_user_lists[this.conference.id]
        };
      } else {
        // No cache
        // Get api key
        const apiKeyResp = await fetch("/api/apiKey");
        let apiKey: string | null = null;
        if (apiKeyResp.ok) {
          apiKey = await apiKeyResp.text();
          apiKey = apiKey.slice(1, -1);
        }
        if (apiKey !== null) {
          // Get users
          const usersResp = await fetch(
            `/api/conferenceUsers?key=${apiKey}&conferenceId=${this.conference.id}`
          );
          if (usersResp.ok) {
            const usersArray = await usersResp.json();
            this.users = Object.fromEntries(
              usersArray.map((user) => [user.id, user])
            );
            // Set cached
            this.setCachedTimestamp();
            return;
          }
        }
        // Something's gone wrong,
        // set users to {} to avoid more issues down the
        // line and invalidate
        this.users = {};
        this.invalidateCachedTimestamp();
      }
    })().then(() => {
      let fn = undefined;
      while ((fn = this.callOrderOnceUsersDefined.shift()) !== undefined) {
        fn();
      }
    });
  }
  private onceUsersDefined(fn: () => void) {
    if (this.users === undefined || this.callOrderOnceUsersDefined.length > 0) {
      this.callOrderOnceUsersDefined.push(fn);
    } else {
      fn();
    }
  }

  public createMentorsMenteesSection(): void {
    this.onceUsersDefined(() => {
      if (this.options.includeSections.mentors) {
        const mentors = $("#confMentors");
        mentors.empty();

        // Populate list of mentors
        for (const id in this.conference.mentors) {
          const person = this.users[id];
          if (person == null) continue;
          if (!anyPathOverlap(this.conference.paths, person.paths)) {
            delete this.conference.mentors[id];
          } else {
            mentors.append(
              $(
                `<div data-id="${id}" class="confMentorCard btn btn-default" style="margin-right: 5px; margin-bottom: 5px;">` +
                  `<input style="margin-right: 5px;" type="checkbox" class="confMentorCardCheckbox"/>` +
                  `<span style="margin-right: 5px;">${escapeHtml(person.name + (person.position == "nonclinical" ? " (Nonclinical)" : ""))}</span>` +
                  `</div>`
              )
            );
          }
        }

        $(".confMentorCard").on("click", (event) => {
          const target = $(event.target);
          if (target.is("div")) {
            target.find("input").trigger("click");
          } else if (target.is("span")) {
            target.parent().find("input").trigger("click");
          }
        });

        $(".confMentorCardCheckbox").on("click", () => {
          const checkedList = $(".confMentorCard > input:checked");
          if (checkedList.length) {
            $(".mentorControls")
              .toggleClass("disabled", false)
              .removeAttr("aria-disabled");
          } else {
            $(".mentorControls")
              .toggleClass("disabled", true)
              .attr("aria-disabled", "true");
          }
        });

        $(".addToAll, .removeFromAll, .deleteMentor").on("click", (event) => {
          this.mentorBulkOperation(event, this.m_mentees);
        });
      }
      if (this.options.includeSections.mentees) {
        const mentees = $("#confMenteeSection");
        mentees.empty();

        // Populate mentee section
        mentees.append(
          $(
            "" +
              "<tr>" +
              '  <td style="padding-bottom: 0px;text-align: center;font-size: 18px;">' +
              '    <div class="dropdown" style="margin-left: 5px;vertical-align: top;display: inline-block; margin-bottom: 10px;">' +
              '      <div class="dropdown-toggle" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="true" style="display: inline-block;">' +
              '        <span class="sortByText" style="margin-right: 5px;">' +
              this.options.verbage.mentee +
              " by " +
              {
                lastName: "Last Name",
                mentor: this.options.verbage.mentor,
                division: "Division"
              }[this.options.sortBy] +
              '</span><span class="caret"></span>' +
              "      </div>" +
              '      <ul class="dropdown-menu">' +
              '        <li class="menteeSortBy" data-type="mentor"><a href="#">Sort By ' +
              this.options.verbage.mentor +
              "</a></li>" +
              '        <li class="menteeSortBy" data-type="lastName"><a href="#">Sort By Last Name</a></li>' +
              '        <li class="menteeSortBy" data-type="division"><a href="#">Sort By Division</a></li>' +
              "      </ul>" +
              "    </div>" +
              '    <div id="messageAll" class="btn btn-primary" style="vertical-align: top; margin-top: -4px; margin-left: 10px; margin-bottom: 5px;">' +
              '      Message All<span class="glyphicon glyphicon-send" style="margin-left: 7px;"></span>' +
              "    </div>" +
              "  </td>" +
              '  <td style="padding-bottom: 10px;padding-left: 20px;text-align: center;font-size: 18px;vertical-align: top;">' +
              this.options.verbage.mentor +
              "</td>" +
              "</tr>"
          )
        );
      }
    });
  }

  public sortMentees(hierarchyNames: { [key: string]: string }) {
    this.onceUsersDefined(() => {
      this.m_mentees = Object.keys(this.users).filter((id) => {
        return (
          !this.users[id].archived &&
          this.users[id].position == "faculty" &&
          anyPathWithin({
            filterList: this.conference.paths,
            targetList: this.users[id].paths
          })
        );
      });
      this.menteeDivisions = {};
      this.m_mentees.forEach((menteeId) => {
        // This reduce is the same as doing a .map().filter()
        this.menteeDivisions[menteeId] = this.users[menteeId].paths
          .reduce((prev, path) => {
            const split = path.split("/")[2];
            if (split != null) prev.push(hierarchyNames[split]);
            return prev;
          }, [])
          .join(", ");
      });
      this.m_mentees = _.orderBy(this.m_mentees, (id: any) => {
        const person = this.users[id];
        const lastName = (person.name || "")
          .split(",")[0]
          .split(" ")
          .slice(-1)[0];
        const mentors = Object.keys(this.conference.mentors).filter(
          (userId) => {
            return (
              this.users[userId] != null &&
              this.conference.mentors[userId][id] != null
            );
          }
        );
        const mentorNames =
          mentors.length == 0
            ? "z"
            : mentors.map((userId) => this.users[userId].name).join(", ");

        if (this.options.sortBy == "lastName") return [lastName];
        if (this.options.sortBy == "mentor") return [mentorNames, lastName];
        if (this.options.sortBy == "division")
          return [this.menteeDivisions[id], mentorNames, lastName];
      });
    });
  }

  public populateMenteesSection(hierarchyNames: { [key: string]: string }) {
    this.onceUsersDefined(() => {
      if (!this.options.includeSections.mentees) return;

      $(".menteeSortBy")
        .off("click")
        .on("click", (event) => {
          this.options.sortBy = getProp(event.target, "type");
          $(".sortByText").text(
            this.options.verbage.mentee +
              " by " +
              {
                lastName: "Last Name",
                mentor: this.options.verbage.mentor,
                division: "Division"
              }[this.options.sortBy]
          );
          this.populateMenteesSection(hierarchyNames);
        });

      this.sortMentees(hierarchyNames);

      $("tr[data-person]").remove();

      this.m_mentees.forEach((menteeId) => {
        const person = this.users[menteeId];
        const mentors = Object.keys(this.conference.mentors).filter(
          (userId) => {
            return (
              this.users[userId] != null &&
              this.conference.mentors[userId][menteeId] != null
            );
          }
        );
        $("#confMenteeSection").append(
          $(
            "" +
              '<tr data-person="' +
              menteeId +
              '" style="border-spacing: 32px;">' +
              '  <td style="border-spacing: 15px;padding-bottom: 10px;">' +
              escapeHtml(person.name) +
              (this.options.sortBy == "division"
                ? " - " + this.menteeDivisions[menteeId]
                : "") +
              " (" +
              escapeHtml(person.email) +
              ")</td>" +
              '  <td style="padding-bottom: 10px;padding-left: 20px;">' +
              '    <div class="dropdown" style="margin-left: 5px;vertical-align: top;display: inline-block;">' +
              '      <button class="btn btn-' +
              (mentors.length == 0 ? "danger" : "default") +
              ' dropdown-toggle" type="button" data-toggle="dropdown" aria-haspopup="true"' +
              '        aria-expanded="false" style="display: inline-block;"><span class="mentorChoicesTitle" style="margin-right: 5px;">' +
              (mentors.length == 0
                ? "None"
                : mentors
                    .map((userId) => escapeHtml(this.users[userId].name))
                    .join(", ")) +
              '</span><span class="caret"></span>' +
              "      </button>" +
              '      <ul class="dropdown-menu">' +
              _.sortBy(
                Object.keys(this.conference.mentors).filter(
                  (userId) => this.users[userId] != null
                ),
                (userId: string) => {
                  const mentor = this.users[userId];
                  const lastName = (mentor.name || "")
                    .split(",")[0]
                    .split(" ")
                    .slice(-1)[0]
                    .trim();
                  return escapeHtml(lastName);
                }
              )
                .map((userId: string) => {
                  return (
                    '<li class="mentorChoice" data-id="' +
                    userId +
                    '">' +
                    '<a href="#">' +
                    '  <input type="checkbox" ' +
                    (this.conference.mentors[userId][menteeId] != null
                      ? "checked"
                      : "") +
                    ' style="margin-right: 10px;vertical-align: top;margin-bottom: 5px;">' +
                    escapeHtml(
                      this.users[userId].name +
                        (this.users[userId].position == "nonclinical"
                          ? " (Nonclinical)"
                          : "")
                    ) +
                    "</a>" +
                    "</li>"
                  );
                })
                .join("") +
              "</ul>" +
              "</div>" +
              "</td>" +
              "</tr>"
          )
        );
      });

      $(".mentorChoice").on("click", (e) => {
        const mentorId = getProp(e.target, "id");
        const personId = getProp(e.target, "person");
        if (getKey(this.conference.mentors, mentorId, {})[personId]) {
          delete this.conference.mentors[mentorId][personId];
        } else this.conference.mentors[mentorId][personId] = true;

        const mentors = Object.keys(this.conference.mentors).filter(
          (userId) => {
            return (
              this.users[userId] != null &&
              this.conference.mentors[userId][personId] != null
            );
          }
        );
        const eventTarget = $(e.target);
        eventTarget
          .closest(".dropdown")
          .find(".mentorChoicesTitle")
          .text(
            mentors.length == 0
              ? "None"
              : mentors.map((userId) => this.users[userId].name).join(", ")
          );
        eventTarget
          .closest(".dropdown")
          .find(".btn")
          .toggleClass("btn-default", mentors.length > 0)
          .toggleClass("btn-danger", mentors.length == 0);
        eventTarget
          .find("input")
          .prop("checked", !!this.conference.mentors[mentorId][personId]);

        this.saveConference(this.conference, { noRefresh: true });
        e.stopPropagation();
      });
    });
  }

  private sendMessageButtonClick() {
    // Directly moved from src/frontent/_Manage.js to here for performance
    // This context has easy access to users list, _Manage.js doesn't.

    const message = String($("#messageInput").val())
      .trim()
      .split("\n")
      .join("<br>");

    if (message.length == 0) {
      $("#messageInput").parent().toggleClass("has-error", true);
      return;
    }

    console.log("emailing");
    $("#sendMessage").text("Sending - Keep Open");

    const messageExtras =
      "&subject=" +
      encodeURIComponent(String($("#messageSubject").val())) +
      "&replyTo=" +
      encodeURIComponent(String($("#messageReplyTo").val()));

    const bundles = [];
    let bundle = [];

    this.m_mentees.map(function (uId) {
      bundle.push(uId);
      if (bundle.length >= 50) {
        bundles.push(bundle);
        bundle = [];
      }
    });
    bundles.push(bundle);

    for2(
      { list: bundles },
      function (bundle: any, done: any, _i: any, wait: any) {
        wait();
        $.get(
          "/blast?users=" +
            bundle.join(",") +
            "&message=" +
            encodeURIComponent(message) +
            messageExtras,
          function () {
            setTimeout(done, 1000);
          }
        );
      },
      function () {
        $("#messageModal").modal("hide");
      }
    );
  }
  public hookMessageButton(user: User) {
    this.onceUsersDefined(() => {
      $("#messageAll")
        .off("click")
        .on("click", () => {
          $("#sendMessage").text("Send");
          $("#messageModal").modal({
            backdrop: "static",
            keyboard: false,
            show: true
          });
          $("#messageInput").val("");

          const numPeople = this.m_mentees.length;
          $("#messageTitle").text(
            "Broadcast Message to " +
              numPeople +
              " " +
              (numPeople === 1 ? "Person" : "People")
          );
          $("#messageSubject").val("EasyCV Message");
          $("#messageReplyTo").val(user.email || "");

          $("#messageInput").parent().toggleClass("has-error", false);

          $("#sendMessage")
            .off("click")
            .on("click", () => {
              this.sendMessageButtonClick();
            });
        });
    });
  }
  createSettingsElements(settingsEl: JQuery, backend?: Backend) {
    this.onceUsersDefined(() => {
      const sections = this.options.includeSections;
      const verbage = this.options.verbage;
      settingsEl.empty();

      // Setup html
      settingsEl.append(
        $(`${
          sections.cutoff
            ? `
            <div style="margin-bottom: 15px;">
            <h4 style="display: inline-block; margin-right: 10px;">Conference Cutoff Date:</h4>
            <input id="confCutoffInput" class="form-control" placeholder="MM/DD" style="display: inline-block;width: 60px;text-align: center;">
            </div>`
            : ""
        }${
          sections.years
            ? `
            <div style="margin-bottom: 15px;">
            <div class="btn btn-default careerYearPrev" style="margin-right: 10px;vertical-align: top;">
            <span class="glyphicon glyphicon-arrow-left"></span>
            </div>
            <h4 id="careerYearTitle" style="display: inline-block;margin-right: 10px;vertical-align: top;margin-top: 8px;margin-bottom: 15px;">1/1/2020 - 12/31/2021</h4>
            <div class="btn btn-default careerYearNext" style="margin-right: 5px; display: inline-block;">
            <span class="glyphicon glyphicon-arrow-right"></span>
            </div>
            <div id="careerYearToggle" class="btn-group" role="group" style="margin-right: 10px; vertical-align: top;">
            <button type="button" class="btn settingToggle btn-default" data-type="on">Open</button>
            <button type="button" class="btn settingToggle btn-default" data-type="off">Closed</button>
            </div>
            </div>`
            : ""
        }${
          sections.reminders
            ? `
            <div style="margin-bottom: 15px;">
              <input type="checkbox" id="weeklyReminders" ${this.conference.weeklyReminders ? "checked" : ""}>
              <label for="weeklyReminders">
                Send weekly reminder emails? (note: this will send reminders now and then weekly on Mondays at 11a EST)
              </label>
            </div>`
            : ""
        }${
          sections.goals
            ? `
          <div style="margin-bottom: 5px;">
            <h4 style="display: inline-block;margin-right: 10px;margin-bottom: 5px;">
              <span style="margin-right: 5px;margin-bottom: 10px;display: inline-block;margin-top: 5px;">Professional Goal Types:</span>
            </h4>
            <div>
              <div id="confGoals" style="display: inline;"></div>
              <div style="display: inline-block;vertical-align: top;margin-right: 10px;margin-bottom: 10px;">
                <div class="input-group" style="width: 155px;">
                  <input id="newConfGoalInput" type="text" class="form-control" style="padding-left: 8px;" placeholder="New Goal Type">
                  <span id="newConfGoal" class="input-group-addon"><span class="glyphicon glyphicon-plus"></span></span>
                </div>
              </div>
            </div>
          </div>`
            : ""
        }${
          sections.questions
            ? `
          <div style="margin-bottom: 15px;">
            <h4 style="display: inline-block;margin-right: 10px;margin-bottom: 10px;">
              <span style="margin-right: 15px;margin-bottom: 10px;display: inline-block;margin-top: 5px;">Other Questions:</span>
              <div id="questionAdd" class="btn btn-default" style="vertical-align: top;">
                <span class="glyphicon glyphicon-plus" style="margin-right: 5px;"></span>
                <span>Question</span>
              </div>
            </h4>
            <div id="confQuestions"></div>
          </div>`
            : ""
        }${
          sections.mentors
            ? `
            <div style="margin-bottom: 15px;">
            <h4 style="display: inline-block;margin-right: 10px;margin-bottom: 10px;">
            <span style="margin-bottom: 10px; margin-right: 10px; display: inline-block;">${verbage.mentor}s:</span>
            <input id="confMentorInput" class="form-control" style="display: inline-block; width: 200px;" placeholder="Add by Name or Email">
            <div class="mentorControls addToAll btn btn-default disabled" aria-disabled="true">Add to All</div>
            <div class="mentorControls removeFromAll btn btn-default disabled" aria-disabled="true">Remove from All</div>
            <div class="mentorControls deleteMentor btn btn-default disabled" aria-disabled="true">Delete</div>
            </h4>
            <div id="confMentors"></div>
            </div>`
            : ""
        }${
          sections.mentees
            ? `
            <div style="margin-bottom: 25px;">
            <table style="border-spacing: 15px;">
            <tbody id="confMenteeSection" style="border-spacing: 15px;"></tbody>
            </table>
            </div>`
            : ""
        }
          `)
      );

      // Setup cutoff input handler
      if (sections.cutoff) {
        const cutoffInput = $("#confCutoffInput");
        cutoffInput.val(this.conference.cutoffDate);
        cutoffInput.toggleClass("has-error", false);
        cutoffInput.off("input").on("input", () => {
          const m_cutoff = String(cutoffInput.val());
          const isErr =
            m_cutoff.indexOf("/") == -1 ||
            !dayjs(m_cutoff, "M/D", true).isValid();
          if (!isErr) {
            this.conference.cutoffDate = m_cutoff;
            this.saveConference(this.conference, { noRefresh: true });
            this.updateConfDate(this.conference);
          }
          cutoffInput.parent().toggleClass("has-error", isErr);
        });
      }
      this.updateConfDate(this.conference);

      if (sections.reminders && backend != undefined) {
        $("#weeklyReminders").on("change", (event) => {
          const weeklyReminders = $(event.target).is(":checked");

          if (
            weeklyReminders &&
            isAnyConferenceOpen(this.conference) &&
            // in case someone adds weekly reminders and forgets
            // it's hard-coded to Lahey text
            this.conference.format.match(/lah_/)
          ) {
            const menteeIds = _.flatMap(
              this.conference.mentors,
              (mentor: string) => {
                return _.keys(mentor);
              }
            );
            const whichYear = mostRecentOpenConference(this.conference);
            backend.send("sendLaheyCCReminders", { menteeIds, whichYear });
          }
          this.conference.weeklyReminders = weeklyReminders;
          this.saveConference(this.conference, { noRefresh: true });
        });
      }

      if (sections.goals) {
        $("#confGoals").empty();
        Object.keys(this.conference.goals).map((goal) => {
          $("#confGoals").append(
            $(
              "" +
                '<div class="btn btn-default goalRemove" data-type="' +
                goal +
                '" style="vertical-align: top;margin-right: 10px;margin-bottom: 10px;">' +
                "  <span>" +
                escapeHtml(goal) +
                "</span>" +
                '  <span class="glyphicon glyphicon-remove" style="margin-left: 5px; margin-top: 2px; vertical-align: top;"></span>' +
                "</div>"
            )
          );
        });

        const thisRef = this;
        $(".goalRemove")
          .off("click")
          .on("click", function () {
            var goal = getProp(this, "type");
            var check = confirm(
              "Are you sure you want to remove " +
                goal +
                " from the list of goal types?"
            );
            if (!check) return;
            delete thisRef.conference.goals[goal];
            thisRef.saveConference(thisRef.conference);
          });

        $("#newConfGoal")
          .off("click")
          .on("click", function () {
            var goal = $("#newConfGoalInput").val();
            thisRef.conference.goals[String(goal)] = true;
            $("#newConfGoalInput").val("");
            thisRef.saveConference(thisRef.conference);
          });
      }
      if (sections.questions) {
        $("#confQuestions").empty();
        this.conference.questions.map((item, i) => {
          $("#confQuestions").append(
            "" +
              '<div data-index="' +
              i +
              '" style="display: block;margin-bottom: 10px;">' +
              '  <input placeholder="New Question" class="questionInput form-control" style="display: inline-block;width: calc(100% - 35px); max-width: 800px; margin-right: 5px;" value="' +
              getQuestionCaption({ cc: this.conference }, null, item) +
              '">' +
              '  <span class="questionRemove glyphicon glyphicon-remove" style="vertical-align: top;margin-top: 8px;"></span>' +
              "</div>"
          );
        });

        $("#questionAdd")
          .off("click")
          .on("click", () => {
            let newKey = getNewQuestionKey(this.conference);
            this.conference.questions.push(newKey);
            this.conference.questionCaptions.push("");
            this.saveConference(this.conference);
          });

        const thisRef = this;
        $(".questionInput")
          .off("input")
          .on("input", function () {
            var index = getProp(this, "index");
            thisRef.conference.questionCaptions[String(index)] = $(this).val();
            thisRef.saveConference(thisRef.conference, { noRefresh: true });
          });

        $(".questionRemove")
          .off("click")
          .on("click", function () {
            var index = getProp(this, "index");
            thisRef.conference.questions.splice(index, 1);
            thisRef.conference.questionCaptions.splice(index, 1);
            thisRef.saveConference(thisRef.conference);
          });
      }
    });
  }
  createAwesomplete() {
    this.onceUsersDefined(() => {
      if (this.options.includeSections.mentors) {
        this.confMentorBox = new Awesomplete($("#confMentorInput")[0], {
          list: [],
          minChars: 1,
          autoFirst: true,
          maxItems: 8,
          data: (userId: Awesomplete.Suggestion, _input: string) => {
            const user = this.users[String(userId)];
            return {
              label: user.name + " (" + user.email + ")",
              value: String(userId)
            };
          }
        } as globalThis.Awesomplete.Options);

        this.confMentorBox.container.addEventListener(
          "awesomplete-selectcomplete",
          (event: Awesomplete.AwesompleteEvent) => {
            const userId = event.text.value;
            getKey(this.conference.mentors, userId, {});
            $("#confMentorInput").val("");
            this.saveConference(this.conference);
          }
        );
        /*
     this.confMentorBox.container.addEventListener(
        "awesomplete-selectcomplete",
        (element) => {
          const userId = element.text.value;
          getKey(this.conference.mentors, userId, {});
          $("#confMentorInput").val("");
          this.saveConference(this.conference);
        }
      );
      */

        this.confMentorBox.list = Object.keys(this.users).filter(
          (id) =>
            !this.users[id].archived &&
            ["faculty", "nonclinical"].includes(this.users[id].position) &&
            anyPathOverlap(this.conference.paths, this.users[id].paths)
        );
      }
    });
  }
  public updateMenteesMentors(htmlRow: JQuery, menteeId: string) {
    this.onceUsersDefined(() => {
      const mentors = Object.keys(this.conference.mentors)
        .filter(
          (mentorId) =>
            Boolean(this.conference.mentors[mentorId]?.[menteeId]) &&
            Boolean(this.users[mentorId])
        )
        .map((mentorId) => this.users[mentorId].name);
      htmlRow
        .find(".mentorChoicesTitle")
        .text(mentors.length ? mentors.join(", ") : "None");
      htmlRow
        .find(".dropdown-toggle")
        .toggleClass("btn-default", Boolean(mentors.length))
        .toggleClass("btn-danger", !Boolean(mentors.length));
    });
  }
  public mentorBulkOperation(event: any, mentees: string[]) {
    this.onceUsersDefined(() => {
      const addIfTrue = $(event.target).hasClass("addToAll");
      const deleteMentor = $(event.target).hasClass("deleteMentor");
      const checkedList = $(".confMentorCard > input:checked").parent();
      if (checkedList.length) {
        let checkedString = "";
        checkedList.each((_index, element) => {
          checkedString += $(element).text() + "; ";
        });
        let confirmation = false;
        if (deleteMentor) {
          confirmation = window.confirm(
            `Are you sure you want to delete ${checkedString} from the list of mentors? This will also unassign them from any current mentees.`
          );
        } else {
          confirmation = window.confirm(
            `Are you sure you want to ${addIfTrue ? "add" : "remove"} ${checkedString} ${addIfTrue ? "to" : "from"} all?`
          );
        }
        if (confirmation) {
          const mentorIds = [];
          checkedList.each((_index, element) => {
            mentorIds.push($(element).attr("data-id"));
          });
          mentorIds.forEach((mentorId) => {
            mentees.forEach((menteeId) => {
              this.conference.mentors[mentorId][menteeId] = addIfTrue
                ? true
                : null;
              const targetRow = $(`tr[data-person="${menteeId}"]`);
              this.updateMenteesMentors(targetRow, menteeId);
              const mentorChoice = targetRow.find(
                `.mentorChoice[data-id="${mentorId}"]`
              );
              deleteMentor
                ? mentorChoice.remove()
                : mentorChoice.find("input").prop("checked", addIfTrue);
            });
            if (deleteMentor) delete this.conference.mentors[mentorId];
          });
          this.saveConference(this.conference, { noRefresh: !deleteMentor });
          event.stopPropagation();
        }
      }
    });
  }
}
